import { Link } from "react-router-dom";

import type {
  AttentionItem,
  DashboardActiveEvent,
  DashboardSummary
} from "@attendik/types/admin-schemas";

import { api } from "../api/client.js";
import { PageHeader } from "../components/AppLayout.js";
import { Badge } from "../components/ui/badge.js";
import { Button } from "../components/ui/button.js";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from "../components/ui/card.js";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "../components/ui/table.js";
import { ErrorState, LoadingState } from "../components/states.js";
import { auditActionLabel, formatDateTime, formatRange } from "../lib/format.js";
import { useAsync } from "../lib/useAsync.js";
import { useScope } from "../store/auth.js";
import { syncStatusVariant } from "./shared/pills.js";
import { PerStageStrip } from "./shared/utilisation.js";

/**
 * Dashboard (build plan P2-T5). A balanced operational overview: headline
 * counts, a "needs attention" panel surfacing failing syncs and capacity
 * pressure, quick actions, the live active-event overview, and recent
 * sync/admin activity.
 *
 * All data comes from one scoped aggregate call (`GET /dashboard`), computed
 * server-side against the shared database — no per-event fan-out from the
 * client.
 */
export function DashboardPage(): JSX.Element {
  const { isPortalAdmin } = useScope();
  const { data, loading, error, reload } = useAsync<DashboardSummary>(
    () => api.getDashboard(),
    []
  );

  if (loading) {
    return (
      <>
        <PageHeader title="Dashboard" />
        <LoadingState />
      </>
    );
  }

  if (error || !data) {
    return (
      <>
        <PageHeader title="Dashboard" />
        <ErrorState message={error ?? "Failed to load."} onRetry={reload} />
      </>
    );
  }

  const { counts, active_events, upcoming_events, attention } = data;

  return (
    <>
      <PageHeader
        title="Dashboard"
        description="What's happening right now, what needs attention, and quick ways in."
        actions={
          <Button asChild>
            <Link to="/events">Create event</Link>
          </Button>
        }
      />

      {/* Row A — metric strip */}
      <div
        className={`mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4 ${
          counts.customers != null ? "lg:grid-cols-5" : ""
        }`}
      >
        <MetricCard label="Active events" value={counts.active_events} />
        <MetricCard label="Upcoming events" value={counts.upcoming_events} />
        <MetricCard label="In room now" value={counts.in_room_now} />
        <MetricCard label="Operators" value={counts.operators} />
        {counts.customers != null ? (
          <MetricCard label="Customers" value={counts.customers} />
        ) : null}
      </div>

      {/* Row B — needs attention + quick actions */}
      <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <AttentionPanel items={attention} />
        </div>
        <QuickActions isPortalAdmin={isPortalAdmin} />
      </div>

      {/* Row C — live operational overview */}
      <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Live now</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {active_events.length === 0 ? (
              <p className="py-4 text-sm text-muted-foreground">
                No events are active right now.
              </p>
            ) : (
              <ul className="divide-y divide-border">
                {active_events.map((event) => (
                  <li key={event.id} className="py-2.5">
                    <Link
                      to={`/events/${event.id}`}
                      className="text-sm font-medium hover:text-accent"
                    >
                      {event.name}
                    </Link>
                    <p className="text-xs text-muted-foreground">
                      {formatRange(event.active_from, event.active_to)}
                    </p>
                    <LiveCountsStrip event={event} />
                    {event.per_stage.length > 0 ? (
                      <div className="mt-2">
                        <PerStageStrip perStage={event.per_stage} />
                      </div>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Upcoming</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {upcoming_events.length === 0 ? (
              <p className="py-4 text-sm text-muted-foreground">
                No upcoming events scheduled.
              </p>
            ) : (
              <ul className="divide-y divide-border">
                {upcoming_events.map((event) => (
                  <li key={event.id} className="py-2.5">
                    <Link
                      to={`/events/${event.id}`}
                      className="text-sm font-medium hover:text-accent"
                    >
                      {event.name}
                    </Link>
                    <p className="text-xs text-muted-foreground">
                      {formatRange(event.active_from, event.active_to)}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Row D — recent activity */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Recent sync runs</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {data.recent_sync_runs.length === 0 ? (
              <p className="py-4 text-sm text-muted-foreground">
                No sync runs yet.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Event</TableHead>
                    <TableHead>Mode</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Started</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.recent_sync_runs.map((run) => (
                    <TableRow key={run.id}>
                      <TableCell>
                        <Link
                          to={`/events/${run.event_id}?tab=integration`}
                          className="font-medium hover:text-accent"
                        >
                          {run.event_name}
                        </Link>
                      </TableCell>
                      <TableCell>{run.mode}</TableCell>
                      <TableCell>
                        <Badge variant={syncStatusVariant(run.status)}>
                          {run.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDateTime(run.started_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Recent admin actions</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {data.recent_audit.length === 0 ? (
              <p className="py-4 text-sm text-muted-foreground">
                No activity yet.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Action</TableHead>
                    <TableHead>Entity</TableHead>
                    <TableHead>When</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.recent_audit.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell>{auditActionLabel(entry.action)}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {entry.entity_type}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDateTime(entry.created_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}

function MetricCard({
  label,
  value
}: {
  label: string;
  value: number;
}): JSX.Element {
  return (
    <Card>
      <CardContent className="pt-6">
        <p className="text-sm text-muted-foreground">{label}</p>
        <p className="mt-1 text-3xl font-semibold tracking-tight">{value}</p>
      </CardContent>
    </Card>
  );
}

/**
 * Needs-attention panel. Lists server-derived issues (failing syncs, capacity
 * pressure) sorted destructive-first, each deep-linking to the relevant event.
 * When nothing is wrong it renders a single muted "all clear" line rather than
 * an empty card.
 */
function AttentionPanel({ items }: { items: AttentionItem[] }): JSX.Element {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Needs attention</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {items.length === 0 ? (
          <p className="py-4 text-sm text-muted-foreground">
            All systems nominal — no issues detected.
          </p>
        ) : (
          <ul className="divide-y divide-border" data-testid="attention-list">
            {items.map((item, i) => (
              <li
                key={`${item.kind}-${item.event_id}-${String(i)}`}
                className="flex items-start gap-3 py-2.5"
              >
                <Badge
                  variant={
                    item.severity === "destructive" ? "destructive" : "warning"
                  }
                >
                  {item.severity === "destructive" ? "Critical" : "Warning"}
                </Badge>
                <div className="min-w-0 flex-1">
                  <Link
                    to={`/events/${item.event_id}${
                      item.tab ? `?tab=${item.tab}` : ""
                    }`}
                    className="text-sm font-medium hover:text-accent"
                  >
                    {item.label}
                  </Link>
                  <p className="truncate text-xs text-muted-foreground">
                    {item.detail}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Quick actions — plain navigation links to the common create flows and key
 * pages. Portal-only destinations are gated by scope.
 */
function QuickActions({
  isPortalAdmin
}: {
  isPortalAdmin: boolean;
}): JSX.Element {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Quick actions</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-2 pt-0">
        <QuickLink to="/events" label="Create event" />
        <QuickLink to="/operators" label="Create operator" />
        {isPortalAdmin ? (
          <QuickLink to="/customers" label="Create customer" />
        ) : null}
        {isPortalAdmin ? (
          <QuickLink to="/admin-users" label="Manage admin users" />
        ) : null}
        <QuickLink to="/audit" label="View audit log" variant="outline" />
      </CardContent>
    </Card>
  );
}

function QuickLink({
  to,
  label,
  variant = "default"
}: {
  to: string;
  label: string;
  variant?: "default" | "outline";
}): JSX.Element {
  return (
    <Button asChild variant={variant} className="justify-start">
      <Link to={to}>{label}</Link>
    </Button>
  );
}

/**
 * Live attendance strip for an active event, read from the aggregate payload
 * (no per-event fetch).
 */
function LiveCountsStrip({
  event
}: {
  event: DashboardActiveEvent;
}): JSX.Element {
  return (
    <dl className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs">
      <LiveCount label="In room" value={event.currently_in} />
      <LiveCount label="Entered" value={event.total_entered} />
      <LiveCount label="Exited" value={event.total_exited} />
      <LiveCount label="Scanned" value={event.total_scanned} />
    </dl>
  );
}

function LiveCount({
  label,
  value
}: {
  label: string;
  value: number;
}): JSX.Element {
  return (
    <div className="flex items-baseline gap-1">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium tabular-nums">{value}</dd>
    </div>
  );
}
